class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.debug = this.dataset.debug === 'true';
    this.log('PredictiveSearch constructor initialised', { element: this });
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.statusElement = this.querySelector('[data-predictive-search-status]');
    this.loadingText = this.dataset.loadingText || 'Loading…';
    this.isInline = this.dataset.inline === 'true';
    this.closeTimeout = null;

    if (!this.input || !this.resultsContainer) {
      this.log('Missing input or results container; predictive search disabled', {
        hasInput: !!this.input,
        hasResults: !!this.resultsContainer,
      });
      return;
    }

    this.resultsContainer.hidden = true;
    this.resultsContainer.classList.remove('is-visible', 'is-loading');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 250),
    );

    this.handleFocusIn = () => {
      if (this.resultsContainer.innerHTML.trim() !== '') {
        this.open();
      }
    };

    this.handleFocusOut = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.close();
    };

    this.handleDocumentClick = (event) => {
      if (this.contains(event.target)) return;
      this.close();
    };

    this.input.addEventListener('focus', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('click', this.handleDocumentClick);

    this.log('Event listeners registered for predictive search');

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.log('Escape key pressed, closing predictive search');
        this.close();
        this.input.blur();
      }
    });
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('focus', this.handleFocusIn);
    }
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('click', this.handleDocumentClick);
    clearTimeout(this.closeTimeout);
    this.log('PredictiveSearch disconnected');
  }

  onChange(event) {
    const searchTerm = event.target.value.trim();
    this.log('Input change detected', { searchTerm });

    if (!searchTerm.length) {
      this.log('Empty search term, closing results');
      this.clearResults(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    this.log('Fetching predictive search results', { searchTerm });
    const searchEndpoint = `/search/suggest?q=${encodeURIComponent(
      searchTerm,
    )}&section_id=predictive-search&resources[type]=product,page&resources[limit]=4`;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.setLoadingState(true);

    fetch(searchEndpoint, { signal: this.abortController.signal })
      .then((response) => {
        this.log('Predictive search response received', { status: response.status });
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        this.log('Predictive search response text parsed');
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#shopify-section-predictive-search');

        if (resultsMarkup) {
          this.log('Predictive search results found', {
            markupLength: resultsMarkup.innerHTML.length,
          });
          this.resultsContainer.innerHTML = resultsMarkup.innerHTML;
          this.open();
        } else {
          this.log('No predictive search markup found in response');
          this.clearResults(true);
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.log('Predictive search fetch error', { error }, 'error');
          console.warn('Predictive search failed', error);
        }
        this.clearResults(true);
      })
      .finally(() => {
        this.setLoadingState(false);
      });
  }

  setLoadingState(isLoading) {
    this.log('Setting predictive search loading state', { isLoading });
    if (this.statusElement) {
      this.statusElement.textContent = isLoading ? this.loadingText : '';
      this.statusElement.classList.toggle('is-loading', isLoading);
    }
    if (this.input) {
      this.input.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    if (this.resultsContainer) {
      this.resultsContainer.classList.toggle('is-loading', isLoading);
    }
  }

  open() {
    this.log('Opening predictive search results panel');
    clearTimeout(this.closeTimeout);
    this.resultsContainer.hidden = false;
    requestAnimationFrame(() => {
      this.resultsContainer.classList.add('is-visible');
    });
    this.input.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('predictive-search:open', { bubbles: true }));
  }

  close(immediate = false) {
    this.log('Closing predictive search results panel', { immediate });
    if (this.resultsContainer.hidden && !this.resultsContainer.classList.contains('is-visible')) {
      return;
    }
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsContainer.classList.remove('is-visible');
    this.resultsContainer.classList.remove('is-loading');
    if (this.statusElement) {
      this.statusElement.classList.remove('is-loading');
      this.statusElement.textContent = '';
    }

    clearTimeout(this.closeTimeout);
    if (immediate) {
      this.resultsContainer.hidden = true;
    } else {
      this.closeTimeout = setTimeout(() => {
        this.resultsContainer.hidden = true;
      }, 140);
    }
    this.dispatchEvent(new CustomEvent('predictive-search:close', { bubbles: true }));
  }

  clearResults(immediate = false) {
    this.log('Clearing predictive search results', { immediate });
    this.resultsContainer.innerHTML = '';
    this.close(immediate);
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  log(message, data = {}, level = 'info') {
    if (!this.debug) return;
    const prefix = '[PredictiveSearch]';
    if (level === 'error') {
      console.error(prefix, message, data);
    } else {
      console.log(prefix, message, data);
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
